import { useState, useEffect } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { API } from "@/context/AuthContext";

const fmt = (v) => (v == null || v === "" ? "—" : typeof v === "number" ? (v < 0 ? `(${Math.abs(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })})` : v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })) : String(v));
const LABELS = { tally_vendor: "Tally vendor", tally_ref: "Tally ref", tally_voucher: "Voucher", tally_date: "Tally date", tally_amount: "Tally value", sap_supplier: "SAP supplier", sap_ref: "SAP ref", sap_miro: "MIRO doc(s)", sap_posting: "Posted", sap_status: "Status", sap_amount: "SAP value", diff: "Difference", match_basis: "Matched on", po: "PO", poi: "Item", gr_value: "Goods receipt", ir_value: "Invoice receipt", status: "Position" };

function Rows({ title, rows, onRow, testId }) {
  if (!rows) return null;
  const cols = rows.length ? Object.keys(rows[0]) : [];
  return (
    <div data-testid={testId}>
      <p className="text-xs font-semibold text-slate-800 mt-3 mb-1">{title} <span className="text-slate-400 font-normal">· {rows.length}</span></p>
      {rows.length === 0 ? <p className="text-xs text-slate-400">None</p> : (
        <div className="max-h-72 overflow-auto rounded border border-slate-100">
          <Table><TableHeader><TableRow>{cols.map((c) => <TableHead key={c} className="text-[10px] uppercase tracking-wider whitespace-nowrap">{LABELS[c] || c}</TableHead>)}</TableRow></TableHeader>
            <TableBody>{rows.map((r, i) => (
              <TableRow key={i} onClick={onRow ? () => onRow(r) : undefined} className={onRow ? "cursor-pointer hover:bg-sky-50" : ""}>
                {cols.map((c) => <TableCell key={c} className={`text-xs py-1.5 whitespace-nowrap ${typeof r[c] === "number" ? "text-right tabular-nums" : ""}`}>{fmt(r[c])}</TableCell>)}
              </TableRow>))}</TableBody></Table>
        </div>)}
    </div>
  );
}

export function RecoDrillDialog({ target, workspace, job, onClose }) {
  const [data, setData] = useState(null);
  const [sub, setSub] = useState(null);
  useEffect(() => {
    setData(null); setSub(null);
    if (!target) return;
    axios.get(`${API}/purchase-reco/run/${workspace}/${job}/detail`, { params: target.params }).then((r) => setData(r.data)).catch((e) => setData({ error: e?.response?.data?.detail || "Could not load detail" }));
  }, [target, workspace, job]);
  useEffect(() => {
    if (!sub) return;
    setSub((s) => ({ ...s, data: null }));
    axios.get(`${API}/purchase-reco/run/${workspace}/${job}/detail`, { params: { po: sub.po, poi: sub.poi } }).then((r) => setSub((s) => s && { ...s, data: r.data })).catch(() => {});
  }, [sub?.po, sub?.poi, workspace, job]); // eslint-disable-line react-hooks/exhaustive-deps
  const poView = sub?.data || (target?.params?.po ? data : null);
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl" data-testid="preco-drill-dialog">
        <DialogHeader><DialogTitle className="text-base">{target?.title}</DialogTitle></DialogHeader>
        {!data && <p className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading documents…</p>}
        {data?.error && <p className="text-sm text-red-600">{data.error}</p>}
        {data?.matched !== undefined && (<>
          <Rows title="Matched invoices — Tally document against SAP MIRO" rows={data.matched} testId="preco-drill-matched" />
          <Rows title="In SAP, no Tally entry" rows={data.sap_only} testId="preco-drill-sap-only" />
          <Rows title="In Tally, no SAP invoice" rows={data.tally_only} testId="preco-drill-tally-only" />
        </>)}
        {data?.po_lines && !sub && <Rows title={`PO lines — ${data.status} (click a line for its GR/IR postings)`} rows={data.po_lines} onRow={(r) => setSub({ po: r.po, poi: r.poi })} testId="preco-drill-po-lines" />}
        {sub && <button type="button" onClick={() => setSub(null)} className="text-xs text-sky-700 hover:underline mt-2" data-testid="preco-drill-back">← back to PO lines</button>}
        {poView && poView.grir_lines !== undefined && (<>
          <Rows title={`PO ${poView.po} item ${poView.poi ?? "all"} — position`} rows={poView.line} />
          <Rows title="GR/IR clearing account lines (WE = goods receipt, RE/KG = invoice receipt)" rows={poView.grir_lines} testId="preco-drill-grir" />
          <Rows title="Material documents (MB51)" rows={poView.material_documents} testId="preco-drill-migo" />
        </>)}
      </DialogContent>
    </Dialog>
  );
}
